import {
  formatWebRtcConnectionMetrics,
  formatWebRtcFps,
  formatWebRtcMilliseconds,
  formatWebRtcPercent,
  formatWebRtcRate,
  formatWebRtcResolution,
  type WebRtcConnectionMetrics,
} from '../rtc-path';
import {
  appendConnectionTrend,
  CONNECTION_TREND_LIMIT,
  summarizeConnectionTrend,
  type ConnectionTrendLevel,
} from '../connection-trend';

let lastVideoResolution: { width: number; height: number } | null = null;
let connectionTrend: ConnectionTrendLevel[] = [];

function isVideoResolution(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value) && value > 0;
}

function setText(id: string, value: string): void {
  const element = document.getElementById(id);
  if (element) element.textContent = value;
}

function updateConnectionTrend(metrics: WebRtcConnectionMetrics): void {
  connectionTrend = appendConnectionTrend(connectionTrend, metrics);
  const summary = summarizeConnectionTrend(connectionTrend);
  setText('session-quality-trend-label', summary.label);
  const bars = document.getElementById('session-quality-trend-bars');
  if (!bars) return;
  bars.replaceChildren();
  const padding = Math.max(0, CONNECTION_TREND_LIMIT - connectionTrend.length);
  for (let index = 0; index < padding; index += 1) {
    const bar = document.createElement('span');
    bar.className = 'session-quality-trend-bar';
    bar.dataset.level = 'empty';
    bar.setAttribute('aria-hidden', 'true');
    bars.appendChild(bar);
  }
  for (const level of connectionTrend) {
    const bar = document.createElement('span');
    bar.className = 'session-quality-trend-bar';
    bar.dataset.level = level;
    bar.setAttribute('aria-hidden', 'true');
    bars.appendChild(bar);
  }
  bars.setAttribute(
    'aria-label',
    summary.observed === 0
      ? '正在收集连接稳定性'
      : `最近 ${summary.observed} 次检查：${summary.label}`,
  );
}

function connectionAdvice(metrics: WebRtcConnectionMetrics): string {
  const state = formatWebRtcConnectionMetrics(metrics);
  if (state === '连接较慢') return '连接有些拥挤，LinkDesk 会优先保证操作响应；需要时可在画面设置中选择“省流量”。';
  if (state === '连接一般') return '连接可以正常使用，偶尔出现等待时会自动调整画面。';
  if (state === '连接顺畅') return '当前连接适合日常操作，画面和操作响应都比较稳定。';
  return '正在收集连接数据，稍后会显示更完整的信息。';
}

export function setConnectionQualityPanelOpen(open: boolean): void {
  const panel = document.getElementById('session-quality-panel');
  const button = document.getElementById('session-quality') as HTMLButtonElement | null;
  if (!panel || !button) return;
  panel.classList.toggle('hidden', !open);
  panel.setAttribute('aria-hidden', String(!open));
  button.setAttribute('aria-expanded', String(open));
  button.setAttribute('aria-label', open ? '关闭连接详情' : '查看连接详情');
  button.title = open ? '关闭连接详情' : '查看连接详情';
  if (open) panel.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
}

export function toggleConnectionQualityPanel(): void {
  const panel = document.getElementById('session-quality-panel');
  setConnectionQualityPanelOpen(Boolean(panel?.classList.contains('hidden')));
}

export function updateConnectionQualityPanel(metrics: WebRtcConnectionMetrics): void {
  const panel = document.getElementById('session-quality-panel');
  if (!panel) return;

  const state = formatWebRtcConnectionMetrics(metrics);
  updateConnectionTrend(metrics);
  panel.dataset.quality = state === '连接较慢' ? 'warn' : state === '连接顺畅' ? 'good' : 'normal';
  const qualityButton = document.getElementById('session-quality') as HTMLButtonElement | null;
  const panelOpen = qualityButton?.getAttribute('aria-expanded') === 'true';
  if (qualityButton) {
    qualityButton.title = panelOpen ? '关闭连接详情' : `${state} · 查看连接详情`;
    qualityButton.setAttribute('aria-label', panelOpen ? '关闭连接详情' : `${state}，查看连接详情`);
  }
  setText('session-quality-panel-state', state);
  setText('session-quality-rtt', formatWebRtcMilliseconds(metrics.controlRttMs ?? metrics.rttMs));
  setText('session-quality-fps', formatWebRtcFps(metrics.framesPerSecond));
  if (isVideoResolution(metrics.videoWidth) && isVideoResolution(metrics.videoHeight)) {
    lastVideoResolution = { width: metrics.videoWidth, height: metrics.videoHeight };
  }
  setText(
    'session-quality-resolution',
    lastVideoResolution
      ? formatWebRtcResolution(lastVideoResolution.width, lastVideoResolution.height)
      : '检测中',
  );
  setText('session-quality-jitter', formatWebRtcMilliseconds(metrics.jitterMs));
  setText('session-quality-loss', formatWebRtcPercent(metrics.packetLossPercent));
  setText('session-quality-upload', formatWebRtcRate(metrics.uploadKbps));
  setText('session-quality-download', formatWebRtcRate(metrics.downloadKbps));
  setText('session-quality-advice', connectionAdvice(metrics));
}

export function resetConnectionQualityPanel(): void {
  lastVideoResolution = null;
  connectionTrend = [];
  updateConnectionQualityPanel({
    rttMs: null,
    controlRttMs: null,
    jitterMs: null,
    packetLossPercent: null,
    framesPerSecond: null,
    framesDropped: null,
    framesDecoded: null,
    jitterBufferMs: null,
    availableOutgoingBitrateKbps: null,
    qualityLimitationReason: null,
    controlBufferedAmount: null,
    uploadKbps: null,
    downloadKbps: null,
  });
  setText('session-quality-route', '正在建立连接');
  setConnectionFirstFrameTime(null);
  setConnectionInputRtt(null);
}

export function setConnectionFirstFrameTime(value: number | null): void {
  setText('session-quality-first-frame', formatWebRtcMilliseconds(value));
}

export function setConnectionInputRtt(value: number | null): void {
  setText('session-quality-input-rtt', formatWebRtcMilliseconds(value));
}

/** 在首个统计样本到达前，用被控端对本次画面设置的实际回执填充反馈。 */
export function setConnectionVideoResolution(width: number | null | undefined, height: number | null | undefined): void {
  if (!isVideoResolution(width) || !isVideoResolution(height)) return;
  lastVideoResolution = { width, height };
  setText('session-quality-resolution', formatWebRtcResolution(width, height));
}
